/**
 * IftFamousFamilies — "India's Great Families" content page.
 *
 * Content adapted from the original indianfamilytree.com marketing
 * page so nothing is lost when the domain routes here. Every family
 * card deep-links to Wikipedia + a YouTube search when a full profile
 * page isn't ready yet.
 *
 * Route: /famous-families (registered in App.js)
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Youtube, Landmark, Sparkles } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const P = {
  bg: "#FDF9EF", ink: "#3A1F0F", inkSoft: "#6B4A2E",
  saffron: "#B25D38", saffronSoft: "#F7D6C1",
  green: "#0E7C55", gold: "#C08A2E", goldSoft: "#F5E6C4",
};

const FAMILIES = [
  { n: "01", slug: "nehru-gandhi", name: "The Nehru–Gandhi Family", origin: "Prayagraj (Allahabad), Uttar Pradesh", kind: "Political", community: "Kashmiri Pandit",
    summary: "Few families have shaped independent India's political landscape as profoundly. Beginning with Motilal Nehru — a prominent lawyer and freedom fighter — the family produced India's first Prime Minister, Jawaharlal Nehru, and later Indira Gandhi and Rajiv Gandhi. Their influence spans constitutional development, foreign policy, education, science and democratic institutions.",
    knownFor: "Politics and public service",
    lineage: ["Motilal Nehru", "Jawaharlal Nehru", "Indira Gandhi", "Rajiv Gandhi", "Rahul & Priyanka Gandhi Vadra"],
    patriarchs: ["Motilal Nehru", "Jawaharlal Nehru"],
    matriarchs: ["Kamala Nehru", "Indira Gandhi", "Sonia Gandhi"],
    wiki: "https://en.wikipedia.org/wiki/Nehru%E2%80%93Gandhi_family",
    yt: "https://www.youtube.com/results?search_query=nehru+gandhi+family+documentary",
    photo: "" },
  { n: "02", slug: "tata", name: "The Tata Family", origin: "Navsari, Gujarat", kind: "Business", community: "Parsi Zoroastrian",
    summary: "The Tatas helped establish modern Indian industry — steel, hospitality, aviation, automobiles, software, healthcare and education. Their philanthropic trusts have funded universities, hospitals, scientific research and social development for more than a century.",
    knownFor: "Industry, philanthropy, education",
    lineage: ["Jamsetji Tata", "Sir Dorabji Tata", "J. R. D. Tata", "Ratan Tata"],
    patriarchs: ["Jamsetji Tata", "J. R. D. Tata", "Ratan Tata"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Tata_family",
    yt: "https://www.youtube.com/results?search_query=jamsetji+tata+documentary",
    photo: "" },
  { n: "03", slug: "birla", name: "The Birla Family", origin: "Pilani, Rajasthan", kind: "Business", community: "Marwari",
    summary: "From a merchant family in Rajasthan, the Birlas became one of India's largest industrial dynasties. Their contributions extend to education, philanthropy, manufacturing and the independence movement — G.D. Birla was a close confidant of Mahatma Gandhi.",
    knownFor: "Cement, metals, financial services, telecom, textiles, education",
    lineage: ["Baldeodas Birla", "Ghanshyam Das Birla", "Basant Kumar Birla", "Aditya Vikram Birla", "Kumar Mangalam Birla"],
    patriarchs: ["Ghanshyam Das Birla", "Basant Kumar Birla"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Birla_family",
    yt: "https://www.youtube.com/results?search_query=birla+family+documentary",
    photo: "" },
  { n: "04", slug: "wadiyar", name: "The Wadiyar Dynasty of Mysore", origin: "Mysuru, Karnataka", kind: "Royal", community: "Arasu (Wodeyar) royal house",
    summary: "Founded in the late 14th century, the Wadiyars transformed Mysore into one of India's most progressive princely states, investing heavily in education, engineering, public infrastructure, hydroelectric power and the arts — including the founding institutions that would become IISc.",
    knownFor: "Progressive princely state — education, engineering, arts",
    lineage: ["Yaduraya Wodeyar", "Raja Wadiyar I", "Krishnaraja Wadiyar III", "Nalwadi Krishnaraja Wadiyar IV", "Yaduveer Krishnadatta Chamaraja Wadiyar"],
    patriarchs: ["Raja Wadiyar I", "Krishnaraja Wadiyar III", "Nalwadi Krishnaraja Wadiyar IV"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Wadiyar_dynasty",
    yt: "https://www.youtube.com/results?search_query=wadiyar+dynasty+mysore+documentary",
    photo: "" },
  { n: "05", slug: "scindia", name: "The Scindia Family", origin: "Gwalior, Madhya Pradesh", kind: "Royal / Political", community: "Maratha (Shinde clan)",
    summary: "The Scindias rose under the Maratha Confederacy and, at their peak, held sway from Gwalior across large parts of northern and central India. In the modern era they have played prominent roles in politics, education and public administration.",
    knownFor: "Maratha empire military leadership, modern Indian politics",
    lineage: ["Ranoji Scindia", "Mahadji Scindia", "Jivajirao Scindia", "Madhavrao Scindia", "Jyotiraditya Scindia"],
    patriarchs: ["Mahadji Scindia", "Madhavrao Scindia", "Jyotiraditya Scindia"],
    matriarchs: ["Vijaya Raje Scindia"],
    wiki: "https://en.wikipedia.org/wiki/Scindia",
    yt: "https://www.youtube.com/results?search_query=scindia+family+documentary",
    photo: "" },
  { n: "06", slug: "holkar", name: "The Holkar Dynasty", origin: "Indore, Madhya Pradesh", kind: "Royal", community: "Maratha (Holkar clan)",
    summary: "Ahilyabai Holkar is remembered as one of India's most admired rulers — she rebuilt temples, roads, pilgrim centers and charitable institutions across India while promoting justice and good governance from her seat at Maheshwar.",
    knownFor: "Just governance, temple rebuilding, women's leadership",
    lineage: ["Malhar Rao Holkar", "Ahilyabai Holkar", "Tukojirao Holkar II", "Yashwant Rao Holkar II"],
    patriarchs: ["Malhar Rao Holkar"],
    matriarchs: ["Ahilyabai Holkar"],
    wiki: "https://en.wikipedia.org/wiki/Holkar_dynasty",
    yt: "https://www.youtube.com/results?search_query=ahilyabai+holkar+documentary",
    photo: "" },
  { n: "07", slug: "gaekwad", name: "The Gaekwad Family", origin: "Vadodara, Gujarat", kind: "Royal", community: "Maratha royal house of Baroda",
    summary: "The Gaekwads modernised Baroda through universal education, libraries, public parks, museums and social reforms — many later adopted across India. Maharaja Sayajirao III founded what would become Maharaja Sayajirao University of Baroda.",
    knownFor: "Social reform, universal education, museums, libraries",
    lineage: ["Pilaji Rao Gaekwad", "Sayajirao Gaekwad III", "Pratapsinhrao Gaekwad", "Ranjitsinh Pratapsinh Gaekwad", "Samarjitsinh Gaekwad"],
    patriarchs: ["Sayajirao Gaekwad III"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Gaekwad_dynasty",
    yt: "https://www.youtube.com/results?search_query=sayajirao+gaekwad+documentary",
    photo: "https://images.pexels.com/photos/30320070/pexels-photo-30320070.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { n: "08", slug: "tagore", name: "The Tagore Family", origin: "Jorasanko, Kolkata, West Bengal", kind: "Cultural", community: "Pirali Brahmin",
    summary: "The Tagores became synonymous with the Bengal Renaissance, producing influential poets, artists, musicians, educators and reformers whose work reshaped Indian literature and culture. Rabindranath Tagore was the first non-European to win the Nobel Prize in Literature (1913).",
    knownFor: "Literature, art, music, education",
    lineage: ["Dwarkanath Tagore", "Debendranath Tagore", "Rabindranath Tagore", "Abanindranath Tagore"],
    patriarchs: ["Dwarkanath Tagore", "Debendranath Tagore"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Tagore_family",
    yt: "https://www.youtube.com/results?search_query=rabindranath+tagore+documentary",
    photo: "" },
  { n: "09", slug: "godrej", name: "The Godrej Family", origin: "Mumbai, Maharashtra", kind: "Business", community: "Parsi Zoroastrian",
    summary: "The Godrej family established one of India's most trusted business groups, known for engineering excellence — from the first locks made in India to environmental stewardship of the vast Godrej mangrove in Mumbai's Vikhroli.",
    knownFor: "Consumer goods, real estate, agriculture, appliances",
    lineage: ["Ardeshir Godrej", "Pirojsha Burjorji Godrej", "Naval Godrej", "Adi Godrej", "Nadir Godrej", "Jamshyd Godrej"],
    patriarchs: ["Ardeshir Godrej", "Pirojsha Godrej"],
    matriarchs: [],
    wiki: "https://en.wikipedia.org/wiki/Godrej_family",
    yt: "https://www.youtube.com/results?search_query=godrej+family+history",
    photo: "" },
  { n: "10", slug: "bajaj", name: "The Bajaj Family", origin: "Rajasthan → Wardha, Maharashtra", kind: "Business", community: "Marwari",
    summary: "Closely associated with Mahatma Gandhi — who called Jamnalal his fifth son — the Bajaj family built a diversified industrial group while supporting education, rural development and philanthropy from Wardha to the world.",
    knownFor: "Automobiles, financial services, rural philanthropy",
    lineage: ["Jamnalal Bajaj", "Kamalnayan Bajaj", "Rahul Bajaj", "Rajiv Bajaj", "Sanjiv Bajaj", "Niraj Bajaj"],
    patriarchs: ["Jamnalal Bajaj", "Rahul Bajaj"],
    matriarchs: ["Jankidevi Bajaj"],
    wiki: "https://en.wikipedia.org/wiki/Bajaj_Group",
    yt: "https://www.youtube.com/results?search_query=jamnalal+bajaj+documentary",
    photo: "" },
];

const OTHER_ROYAL_HOUSES = [
  { name: "Sisodia Dynasty", where: "Mewar, Rajasthan", url: "https://en.wikipedia.org/wiki/Sisodia" },
  { name: "Rathore Dynasty", where: "Marwar, Rajasthan", url: "https://en.wikipedia.org/wiki/Rathore" },
  { name: "Kachwaha Dynasty", where: "Jaipur, Rajasthan", url: "https://en.wikipedia.org/wiki/Kachhwaha" },
  { name: "Travancore Royal Family", where: "Kerala", url: "https://en.wikipedia.org/wiki/Travancore_royal_family" },
  { name: "Cochin Royal Family", where: "Kerala", url: "https://en.wikipedia.org/wiki/Cochin_royal_family" },
  { name: "Bhosale Dynasty", where: "Nagpur, Maharashtra", url: "https://en.wikipedia.org/wiki/Bhonsle" },
  { name: "Bhonsle Lineage of Chhatrapati Shivaji", where: "Satara & Kolhapur", url: "https://en.wikipedia.org/wiki/Bhonsle" },
  { name: "Ahom Royal Family", where: "Assam", url: "https://en.wikipedia.org/wiki/Ahom_dynasty" },
  { name: "Namgyal Dynasty", where: "Ladakh", url: "https://en.wikipedia.org/wiki/Namgyal_dynasty" },
  { name: "Chogyal Dynasty", where: "Former Kingdom of Sikkim", url: "https://en.wikipedia.org/wiki/Chogyal" },
];

const SCHEMA = [
  ["Family Name", "Official family or dynasty name"],
  ["Family Type", "Royal, Political, Business, Cultural, Religious, Community"],
  ["Origin", "Village, town, district, state, country"],
  ["Community / Lineage", "Clan, dynasty, community or traditional lineage (where historically documented)"],
  ["Founder / Ancestor", "Earliest known notable ancestor"],
  ["Patriarchs", "Key male figures across generations"],
  ["Matriarchs", "Key female figures across generations"],
  ["Family Tree", "Interactive genealogical chart"],
  ["Timeline", "Major events by generation"],
  ["Heritage Sites", "Palaces, homes, temples, estates or businesses associated with the family"],
  ["Notable Members", "Biographies of prominent descendants"],
  ["Contributions", "Politics, arts, science, business, education, philanthropy, public service"],
  ["Photo Gallery", "Historic and contemporary images"],
  ["Documents", "Letters, records, certificates, memoirs, archival material"],
  ["Sources", "Books, archives, academic research, government records and reputable historical references"],
];

export default function IftFamousFamilies() {
  useEffect(() => { document.title = "India's Great Families | IndianFamilyTree.com"; }, []);
  return (
    <div style={{ background: P.bg, color: P.ink }} data-testid="ift-famous-families">
      <SeoHead
        section="genealogy"
        title="India's Great Families — Lineages That Shaped a Civilisation"
        description="Ten of India's most influential lineages — Nehru–Gandhi, Tata, Birla, Wadiyar, Scindia, Holkar, Gaekwad, Tagore, Godrej and Bajaj — plus historic royal houses across the subcontinent."
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: P.saffronSoft, background: `linear-gradient(180deg, ${P.saffronSoft} 0%, ${P.bg} 100%)` }}>
        <div className="max-w-5xl mx-auto px-4 py-14">
          <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: P.saffron }}>A SocialPulse Heritage Project</div>
          <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mt-2 mb-4" style={{ color: P.ink }}>
            India's Great Families — lineages that shaped a civilisation.
          </h1>
          <p className="text-base sm:text-lg" style={{ color: P.inkSoft }}>
            India's history is often told through empires, movements, literature, science, business and culture. Behind these achievements stand remarkable families whose influence has extended across generations. Some trace their roots back over a thousand years; others emerged during India's modern industrial and democratic eras. Together, these lineages tell the story of a nation built on tradition, resilience, innovation and service.
          </p>
        </div>
      </section>

      {/* Index */}
      <section className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: P.saffron }}>The index</div>
        <h2 className="font-display text-2xl mb-4" style={{ color: P.ink }}>Ten lineages, in order of appearance.</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {FAMILIES.map((f) => (
            <a href={`#${f.slug}`} key={f.slug} className="rounded-xl border p-3 hover:shadow-sm transition bg-white" style={{ borderColor: P.saffronSoft }} data-testid={`ift-fam-index-${f.slug}`}>
              <div className="text-[10px] font-mono opacity-60">{f.n}</div>
              <div className="font-display text-sm leading-tight mt-1">{f.name.replace("The ", "").replace(" Family", "").replace(" Dynasty", "")}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider mt-1" style={{ color: P.saffron }}>{f.kind}</div>
            </a>
          ))}
        </div>
      </section>

      {/* Family cards */}
      <section className="max-w-5xl mx-auto px-4 pb-12 space-y-10">
        {FAMILIES.map((f) => (
          <article id={f.slug} key={f.slug} className="rounded-2xl border bg-white p-5 sm:p-6 scroll-mt-24" style={{ borderColor: P.saffronSoft }} data-testid={`ift-fam-card-${f.slug}`}>
            <div className="grid sm:grid-cols-[220px_1fr] gap-5">
              <img src={f.photo} alt={f.name} className="w-full h-52 sm:h-full object-cover rounded-xl" loading="lazy" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>{f.origin} · {f.kind}</div>
                <div className="text-[10px] font-mono opacity-60">{f.n} · {f.community}</div>
                <h3 className="font-display text-2xl mt-1 mb-2" style={{ color: P.ink }}>{f.name}</h3>
                <p className="text-sm mb-3" style={{ color: P.inkSoft }}>{f.summary}</p>
                <div className="grid sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Known for</div>
                    <div>{f.knownFor}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Origin</div>
                    <div>{f.origin}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Family lineage</div>
                  <ol className="text-xs space-y-0.5">
                    {f.lineage.map((l, i) => (
                      <li key={i} className="flex gap-2"><span className="opacity-50 w-6">{String((i * 2) + 1).padStart(2, "0")}</span>{l}</li>
                    ))}
                  </ol>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4 text-xs">
                  {f.patriarchs.length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Patriarchs</div>
                      <ul className="space-y-0.5">{f.patriarchs.map((p, i) => <li key={i}>· {p}</li>)}</ul>
                    </div>
                  )}
                  {f.matriarchs.length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Matriarchs</div>
                      <ul className="space-y-0.5">{f.matriarchs.map((m, i) => <li key={i}>· {m}</li>)}</ul>
                    </div>
                  )}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a href={f.wiki} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: P.saffron, color: P.saffron }}>
                    Wikipedia <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href={f.yt} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: P.saffronSoft, color: P.ink }}>
                    <Youtube className="w-3 h-3" /> Watch on YouTube
                  </a>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Beyond the ten */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Historic royal houses</div>
        <h2 className="font-display text-2xl mb-4" style={{ color: P.ink }}>Beyond the ten — palaces still standing.</h2>
        <p className="text-sm mb-5" style={{ color: P.inkSoft }}>
          India remains home to numerous historic royal lineages whose cultural influence continues today, from Rajasthan's Aravallis to the Himalayas of Sikkim and Ladakh.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {OTHER_ROYAL_HOUSES.map((r) => (
            <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 rounded-xl border p-3 bg-white hover:shadow-sm transition" style={{ borderColor: P.saffronSoft }}>
              <Landmark className="w-4 h-4 mt-0.5 shrink-0" style={{ color: P.saffron }} />
              <div>
                <div className="font-display text-sm">{r.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{r.where}</div>
              </div>
              <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
            </a>
          ))}
        </div>
      </section>

      {/* Archive schema */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>The archive schema</div>
        <h2 className="font-display text-2xl mb-4" style={{ color: P.ink }}>What every family profile holds.</h2>
        <p className="text-sm mb-5" style={{ color: P.inkSoft }}>
          A consistent schema across IndianFamilyTree.com — so a Kalpathy tharavad and the Wadiyar dynasty of Mysore can live side-by-side in the same living archive.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {SCHEMA.map(([k, v]) => (
            <div key={k} className="rounded-xl border p-3 bg-white" style={{ borderColor: P.saffronSoft }}>
              <div className="font-display text-sm">{k}</div>
              <div className="text-xs mt-0.5" style={{ color: P.inkSoft }}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <div className="rounded-3xl border p-8 md:p-10 text-center" style={{ borderColor: P.saffronSoft, background: `linear-gradient(135deg, ${P.saffronSoft} 0%, ${P.bg} 55%, ${P.goldSoft} 100%)` }}>
          <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: P.saffron }} />
          <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Preserving family heritage</div>
          <h3 className="font-display text-2xl md:text-3xl mb-3" style={{ color: P.ink }}>Every family holds a unique story.</h3>
          <p className="text-sm md:text-base max-w-2xl mx-auto mb-5" style={{ color: P.inkSoft }}>
            India's history is not only recorded in palaces, archives and monuments, but also in family photographs, oral traditions, handwritten letters, migration journeys and cherished memories passed from one generation to the next. IndianFamilyTree.com — part of the SocialPulse heritage ecosystem — helps you document your tree, ancestral home, village history and personal narratives so India's living archive keeps growing.
          </p>
          <Link to="/family-trees/new" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm" style={{ background: P.saffron }} data-testid="ift-famous-cta">
            Start your family tree <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
